import { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Input, Select, Switch, Tabs, Button, Collapse, Spin, Alert } from 'antd';
import { t } from '../../i18n/i18n';
import {
  useCreateScreeningCustomStep,
  useUpdateScreeningCustomStep,
} from '../../hooks/useScreeningCustomSteps';
import { useTestGroupsInfinite } from '../../hooks/useRecruitSessions';
import { SCREENING_STEP_TYPES } from '../../models/screeningCustomStep';
import useTranslations from '../../hooks/useTranslations';
import GenericToggle from '../GenericToggle/GenericToggle';
import { GenericTextArea } from '../GenericTextArea/GenericTextArea';
import { TestGroupTypes, ReviewTypeEnum } from '../../constants/enums';

const { Option } = Select;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const ScreeningCustomStepModal = ({
  visible,
  customStep,
  recruitSessions,
  testGroups,
  workspaceId,
  onClose,
  onSuccess,
  onRecruitSessionsScroll,
  isFetchingMoreRecruitSessions,
}) => {
  const [form] = Form.useForm();
  const { key: langKey } = useTranslations();
  const [selectedStep, setSelectedStep] = useState(customStep?.step || SCREENING_STEP_TYPES.START);
  const [showButton, setShowButton] = useState(customStep?.button?.show || false);
  const [isAdvanceSetting, setIsAdvanceSetting] = useState(false);
  const [selectedScreeningId, setSelectedScreeningId] = useState(customStep?.screeningId || null);
  const [selectedTestGroupKey, setSelectedTestGroupKey] = useState(
    customStep?.testGroupGrouperKey || null
  );

  // React Query mutations
  const createMutation = useCreateScreeningCustomStep();
  const updateMutation = useUpdateScreeningCustomStep();

  // Find selected session to get levelId
  const selectedSession = useMemo(() => {
    if (!selectedScreeningId || !recruitSessions) return null;
    return recruitSessions.find(
      (session) =>
        session.screeningId === selectedScreeningId ||
        session.id === selectedScreeningId ||
        session.recruitSessionStageId === selectedScreeningId ||
        session.levelId === selectedScreeningId
    );
  }, [selectedScreeningId, recruitSessions]);
  // Fetch test groups for selected screening to check for qualitative tests
  const {
    data: selectedScreeningTestGroupsData,
    isFetching: isLoadingScreeningTestGroups,
    hasNextPage: hasNextTestGroupsPage,
    fetchNextPage: fetchNextTestGroupsPage,
    isFetchingNextPage: isFetchingNextTestGroupsPage,
  } = useTestGroupsInfinite(
    {
      levelId:
        selectedSession?.stages?.[0]?.levelId ||
        selectedSession?.levelId ||
        selectedSession?.screeningId ||
        null,
      workspaceId,
    },
    {
      enabled: !!selectedScreeningId,
    }
  );

  const selectedScreeningTestGroups = useMemo(() => {
    if (!selectedScreeningId) return [];
    if (!selectedScreeningTestGroupsData?.pages) return [];
    return selectedScreeningTestGroupsData.pages.flatMap((page) => page?.collection || []);
  }, [selectedScreeningTestGroupsData, selectedScreeningId]);

  const availableTestGroups = useMemo(() => {
    if (selectedScreeningId && selectedScreeningTestGroups.length > 0) {
      return selectedScreeningTestGroups;
    }
    return testGroups || [];
  }, [selectedScreeningId, selectedScreeningTestGroups, testGroups]);

  const isEvaluationBlocked = useMemo(() => {
    // Check if there's a selected session (evaluation)
    if (selectedSession) {
      if (selectedSession.reviewType === ReviewTypeEnum.PSYCHOTECHNICS) return true;
      if (selectedScreeningTestGroups.length > 0) {
        return selectedScreeningTestGroups.some(
          (item) => item?.type === TestGroupTypes.QUALITATIVE
        );
      }
    }

    // Check if a test group is selected directly (without evaluation)
    if (selectedTestGroupKey) {
      const selectedTestGroup = availableTestGroups.find(
        (tg) => tg.grouperKey === selectedTestGroupKey
      );
      if (selectedTestGroup?.type === TestGroupTypes.QUALITATIVE) {
        return true;
      }
    }

    return false;
  }, [selectedSession, selectedScreeningTestGroups, selectedTestGroupKey, availableTestGroups]);

  const handleTestGroupsScroll = (e) => {
    const { target } = e;
    if (
      target.scrollTop + target.offsetHeight === target.scrollHeight &&
      hasNextTestGroupsPage &&
      !isFetchingNextTestGroupsPage
    ) {
      fetchNextTestGroupsPage();
    }
  };

  useEffect(() => {
    if (customStep) {
      setSelectedScreeningId(customStep.screeningId || null);
      setSelectedTestGroupKey(customStep.testGroupGrouperKey || null);
      const formValues = {
        step: customStep.step,
        screeningId: customStep.screeningId,
        testGroupGrouperKey: customStep.testGroupGrouperKey,
        showSubmitDialog: customStep.showSubmitDialog ?? true,
        showSkipDialog: customStep.showSkipDialog ?? true,
        showDemoTestsButton: customStep.showDemoTestsButton ?? true,
      };

      if (typeof customStep.subtitle === 'string') {
        formValues.subtitle_es = customStep.subtitle;
        formValues.subtitle_en = customStep.subtitle;
        formValues.subtitle_pt = customStep.subtitle;
      } else if (customStep.subtitle) {
        formValues.subtitle_es = customStep.subtitle.es || '';
        formValues.subtitle_en = customStep.subtitle.en || '';
        formValues.subtitle_pt = customStep.subtitle.pt || '';
      }

      if (customStep.button) {
        formValues.button_show = customStep.button.show ?? true;
        formValues.button_link = customStep.button.link || '';
        setShowButton(customStep.button.show ?? false);

        if (typeof customStep.button.title === 'string') {
          formValues.button_title_es = customStep.button.title;
          formValues.button_title_en = customStep.button.title;
          formValues.button_title_pt = customStep.button.title;
        } else if (customStep.button.title) {
          formValues.button_title_es = customStep.button.title.es || '';
          formValues.button_title_en = customStep.button.title.en || '';
          formValues.button_title_pt = customStep.button.title.pt || '';
        }
      }

      if (customStep.demoTestsButtonLabels) {
        formValues.demo_title_es = customStep.demoTestsButtonLabels.title?.es || '';
        formValues.demo_title_en = customStep.demoTestsButtonLabels.title?.en || '';
        formValues.demo_title_pt = customStep.demoTestsButtonLabels.title?.pt || '';
        formValues.demo_subtitleBefore_es =
          customStep.demoTestsButtonLabels.subtitleBefore?.es || '';
        formValues.demo_subtitleBefore_en =
          customStep.demoTestsButtonLabels.subtitleBefore?.en || '';
        formValues.demo_subtitleBefore_pt =
          customStep.demoTestsButtonLabels.subtitleBefore?.pt || '';
        formValues.demo_subtitleAfter_es = customStep.demoTestsButtonLabels.subtitleAfter?.es || '';
        formValues.demo_subtitleAfter_en = customStep.demoTestsButtonLabels.subtitleAfter?.en || '';
        formValues.demo_subtitleAfter_pt = customStep.demoTestsButtonLabels.subtitleAfter?.pt || '';
        formValues.demo_clickText_es = customStep.demoTestsButtonLabels.clickText?.es || '';
        formValues.demo_clickText_en = customStep.demoTestsButtonLabels.clickText?.en || '';
        formValues.demo_clickText_pt = customStep.demoTestsButtonLabels.clickText?.pt || '';
      }

      form.setFieldsValue(formValues);
      setSelectedStep(customStep.step);
    } else {
      setSelectedScreeningId(null);
      setSelectedTestGroupKey(null);
      form.resetFields();
      setShowButton(false);
      setSelectedStep(SCREENING_STEP_TYPES.START);
    }
  }, [customStep, form]);

  const handleSubmit = async (values) => {
    const payload = {
      step: values.step,
      workspaceId,
      screeningId: values.screeningId || null,
      testGroupGrouperKey: values.testGroupGrouperKey || null,
    };

    if (values.subtitle_es || values.subtitle_en || values.subtitle_pt) {
      payload.subtitle = {
        es: values.subtitle_es || '',
        en: values.subtitle_en || '',
        pt: values.subtitle_pt || '',
      };
    }

    if (
      values.step === SCREENING_STEP_TYPES.START ||
      values.step === SCREENING_STEP_TYPES.SETTINGS
    ) {
      payload.showSubmitDialog = values.showSubmitDialog === true;
      payload.showSkipDialog = values.showSkipDialog === true;
      payload.showDemoTestsButton = values.showDemoTestsButton === true;

      // demoTestsButtonLabels is independent from showDemoTestsButton
      if (
        values.demo_title_es ||
        values.demo_title_en ||
        values.demo_title_pt ||
        values.demo_subtitleBefore_es ||
        values.demo_subtitleBefore_en ||
        values.demo_subtitleBefore_pt ||
        values.demo_subtitleAfter_es ||
        values.demo_subtitleAfter_en ||
        values.demo_subtitleAfter_pt ||
        values.demo_clickText_es ||
        values.demo_clickText_en ||
        values.demo_clickText_pt
      ) {
        payload.demoTestsButtonLabels = {
          title: {
            es: values.demo_title_es || '',
            en: values.demo_title_en || '',
            pt: values.demo_title_pt || '',
          },
          subtitleBefore: {
            es: values.demo_subtitleBefore_es || '',
            en: values.demo_subtitleBefore_en || '',
            pt: values.demo_subtitleBefore_pt || '',
          },
          subtitleAfter: {
            es: values.demo_subtitleAfter_es || '',
            en: values.demo_subtitleAfter_en || '',
            pt: values.demo_subtitleAfter_pt || '',
          },
          clickText: {
            es: values.demo_clickText_es || '',
            en: values.demo_clickText_en || '',
            pt: values.demo_clickText_pt || '',
          },
        };
      }
    }

    if (values.button_show) {
      payload.button = {
        show: true,
        title: {
          es: values.button_title_es || '',
          en: values.button_title_en || '',
          pt: values.button_title_pt || '',
        },
        link: values.button_link || '',
      };
    } else {
      payload.button = {
        show: false,
        title: {
          es: '',
          en: '',
          pt: '',
        },
        link: '',
      };
    }

    try {
      if (customStep?._id) {
        await updateMutation.mutateAsync({ id: customStep._id, customStep: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onSuccess();
    } catch (error) {
      // Error is handled by the mutation hooks with notifications
    }
  };

  const getDisplayLanguage = () => {
    const selectedScreeningId = form.getFieldValue('screeningId');
    if (selectedScreeningId) {
      const selectedSession = (recruitSessions || []).find(
        (session) => (session.recruitSessionStageId || session.id) === selectedScreeningId
      );
      if (selectedSession?.language) {
        return selectedSession.language;
      }
    }

    if (langKey) {
      return langKey;
    }

    return 'es';
  };
  const renderTranslatedFields = (fieldPrefix, label, isTextArea = false) => {
    const displayLanguage = getDisplayLanguage();

    if (isAdvanceSetting) {
      return (
        <Tabs defaultActiveKey={displayLanguage}>
          <TabPane tab="Español" key="es">
            <Form.Item name={`${fieldPrefix}_es`} label={`${label} (ES)`}>
              {isTextArea ? (
                <GenericTextArea
                  placeholder={t('CREATE_TESTGROUP_ASSIGNMENT_PLACEHOLDER')}
                  onChange={(e) => {
                    const normalizedValue = e === '<p><br></p>' ? '' : e;
                    form.setFieldValue(`${fieldPrefix}_es`, normalizedValue);
                  }}
                  richTextClassName="h-75 mb-2 pb-5"
                  value={form.getFieldValue(`${fieldPrefix}_es`) || ''}
                  maxLength={5000}
                  richText
                  fontsType={['bold', 'italic', 'underline']}
                  link={['link']}
                />
              ) : (
                <Input />
              )}
            </Form.Item>
          </TabPane>
          <TabPane tab="English" key="en">
            <Form.Item name={`${fieldPrefix}_en`} label={`${label} (EN)`}>
              {isTextArea ? (
                <GenericTextArea
                  placeholder={t('CREATE_TESTGROUP_ASSIGNMENT_PLACEHOLDER')}
                  onChange={(e) => {
                    const normalizedValue = e === '<p><br></p>' ? '' : e;
                    form.setFieldValue(`${fieldPrefix}_en`, normalizedValue);
                  }}
                  richTextClassName="h-75 mb-2 pb-5"
                  value={form.getFieldValue(`${fieldPrefix}_en`) || ''}
                  maxLength={5000}
                  richText
                  fontsType={['bold', 'italic', 'underline']}
                  link={['link']}
                />
              ) : (
                <Input />
              )}
            </Form.Item>
          </TabPane>
          <TabPane tab="Português" key="pt">
            <Form.Item name={`${fieldPrefix}_pt`} label={`${label} (PT)`}>
              {isTextArea ? (
                <GenericTextArea
                  placeholder={t('CREATE_TESTGROUP_ASSIGNMENT_PLACEHOLDER')}
                  onChange={(e) => {
                    const normalizedValue = e === '<p><br></p>' ? '' : e;
                    form.setFieldValue(`${fieldPrefix}_pt`, normalizedValue);
                  }}
                  richTextClassName="h-75 mb-2 pb-5"
                  value={form.getFieldValue(`${fieldPrefix}_pt`) || ''}
                  maxLength={5000}
                  richText
                  fontsType={['bold', 'italic', 'underline']}
                  link={['link']}
                />
              ) : (
                <Input />
              )}
            </Form.Item>
          </TabPane>
        </Tabs>
      );
    }

    return (
      <Form.Item
        name={`${fieldPrefix}_${displayLanguage}`}
        label={`${label} (${displayLanguage.toUpperCase()})`}
      >
        {isTextArea ? (
          <GenericTextArea
            placeholder={t('CREATE_TESTGROUP_ASSIGNMENT_PLACEHOLDER')}
            onChange={(e) => {
              const normalizedValue = e === '<p><br></p>' ? '' : e;
              form.setFieldValue(`${fieldPrefix}_${displayLanguage}`, normalizedValue);
            }}
            richTextClassName="h-75 mb-2 pb-5"
            value={form.getFieldValue(`${fieldPrefix}_${displayLanguage}`) || ''}
            maxLength={5000}
            richText
            fontsType={['bold', 'italic', 'underline']}
            link={['link']}
          />
        ) : (
          <Input />
        )}
      </Form.Item>
    );
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      open={visible}
      title={customStep ? t('SCREENING_CUSTOM_STEP_EDIT') : t('SCREENING_CUSTOM_STEP_CREATE')}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={isLoading}>
          {t('CANCEL')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isLoading}
          disabled={isEvaluationBlocked || isLoadingScreeningTestGroups}
          onClick={() => form.submit()}
        >
          {customStep ? t('UPDATE') : t('CREATE')}
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          step: SCREENING_STEP_TYPES.START,
          button_show: false,
          showSubmitDialog: true,
          showSkipDialog: true,
          showDemoTestsButton: true,
        }}
      >
        <Form.Item
          name="step"
          label={t('SCREENING_CUSTOM_STEP_TYPE')}
          rules={[{ required: true, message: t('FIELD_REQUIRED') }]}
        >
          <Select onChange={setSelectedStep}>
            {Object.values(SCREENING_STEP_TYPES).map((type) => (
              <Option key={type} value={type}>
                {t(`SCREENING_STEP_TYPE_${type}`)}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="screeningId"
          label={t('SCREENING_CUSTOM_STEP_EVALUATION')}
          extra={t('SCREENING_CUSTOM_STEP_EVALUATION_HELP')}
        >
          <Select
            allowClear
            showSearch
            placeholder={t('SCREENING_CUSTOM_STEP_ALL_WORKSPACE')}
            onChange={(value) => {
              setSelectedScreeningId(value || null);
              form.validateFields();
            }}
            onPopupScroll={onRecruitSessionsScroll}
            filterOption={(input, option) => {
              const searchText = option?.label || '';
              return searchText.toLowerCase().includes(input.toLowerCase());
            }}
            dropdownRender={(menu) => (
              <>
                {menu}
                {isFetchingMoreRecruitSessions && (
                  <div style={{ textAlign: 'center', padding: '8px' }}>
                    <Spin size="small" />
                  </div>
                )}
              </>
            )}
          >
            {(recruitSessions || []).map((session) => {
              const sessionId =
                session.levelId ||
                session.screeningId ||
                session.recruitSessionStageId ||
                session.id;
              const sessionName =
                session.recruitSessionStageName ||
                session.nameTranslated?.[langKey] ||
                session.name;
              return (
                <Option key={sessionId} value={sessionId} label={sessionName}>
                  {sessionName}
                </Option>
              );
            })}
          </Select>
        </Form.Item>

        {isEvaluationBlocked && (
          <Alert
            message={t('SCREENING_CUSTOM_STEP_BLOCKED_EVALUATION_TITLE')}
            description={t('SCREENING_CUSTOM_STEP_BLOCKED_EVALUATION_DESC')}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item
          name="testGroupGrouperKey"
          label={t('SCREENING_CUSTOM_STEP_TEST_GROUP')}
          extra={t('SCREENING_CUSTOM_STEP_TEST_GROUP_HELP')}
        >
          <Select
            allowClear
            showSearch
            loading={isLoadingScreeningTestGroups}
            placeholder={t('SCREENING_CUSTOM_STEP_TEST_GROUP_NONE')}
            onChange={(value) => setSelectedTestGroupKey(value || null)}
            onPopupScroll={handleTestGroupsScroll}
            filterOption={(input, option) => {
              const searchText = option?.label || '';
              return searchText.toLowerCase().includes(input.toLowerCase());
            }}
            dropdownRender={(menu) => (
              <>
                {menu}
                {isFetchingNextTestGroupsPage && (
                  <div style={{ textAlign: 'center', padding: '8px' }}>
                    <Spin size="small" />
                  </div>
                )}
              </>
            )}
            notFoundContent={
              isLoadingScreeningTestGroups ? (
                <div style={{ textAlign: 'center', padding: '8px' }}>
                  <Spin size="small" />
                </div>
              ) : null
            }
          >
            {Array.isArray(availableTestGroups) &&
              availableTestGroups.map((testGroup) => (
                <Option
                  key={testGroup.grouperKey}
                  value={testGroup.grouperKey}
                  label={testGroup.name}
                >
                  {testGroup.name}
                </Option>
              ))}
          </Select>
        </Form.Item>

        <div className="d-flex justify-content-end w-100">
          <GenericToggle
            defaultActive={isAdvanceSetting}
            actionFunction={() => {
              setIsAdvanceSetting(!isAdvanceSetting);
              setShowButton(false);
              form.setFieldValue('button_show', false);
            }}
            leftText={t('BASIC_STEP_SETTINGS')}
            rightText={t('ADVANCE_STEP_SETTINGS')}
          />
        </div>
        <Collapse defaultActiveKey={['content', 'settings']}>
          {selectedStep !== SCREENING_STEP_TYPES.SETTINGS && (
            <Panel header={t('SCREENING_CUSTOM_STEP_CONTENT')} key="content">
              <div className="mb-3">
                <label className="font-weight-600">{t('SCREENING_CUSTOM_STEP_SUBTITLE')}</label>
                {renderTranslatedFields('subtitle', t('SUBTITLE'), true)}
              </div>

              {isAdvanceSetting && (
                <Form.Item
                  name="button_show"
                  label={t('SCREENING_CUSTOM_STEP_SHOW_BUTTON')}
                  valuePropName="checked"
                >
                  <Switch onChange={setShowButton} />
                </Form.Item>
              )}

              {showButton && (
                <>
                  <div className="mb-3">
                    <label className="font-weight-600">
                      {t('SCREENING_CUSTOM_STEP_BUTTON_TITLE')}
                    </label>
                    {renderTranslatedFields('button_title', t('BUTTON_TEXT'))}
                  </div>

                  <Form.Item
                    name="button_link"
                    label={t('SCREENING_CUSTOM_STEP_BUTTON_LINK')}
                    rules={[
                      { required: showButton, message: t('FIELD_REQUIRED') },
                      { type: 'url', message: t('INVALID_URL') },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </>
              )}
            </Panel>
          )}

          {selectedStep === SCREENING_STEP_TYPES.SETTINGS && (
            <Panel header={t('SCREENING_CUSTOM_STEP_SETTINGS_CONFIG')} key="settings">
              {/* <Form.Item
                name="showSubmitDialog"
                label={t('SCREENING_CUSTOM_STEP_SHOW_SUBMIT_DIALOG')}
                valuePropName="checked"
              >
                <Switch /> HIDDEN TO AVOID PARTNER ACCESS
              </Form.Item> */}

              <Form.Item
                name="showSkipDialog"
                label={t('SCREENING_CUSTOM_STEP_SHOW_SKIP_DIALOG')}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="showDemoTestsButton"
                label={t('SCREENING_CUSTOM_STEP_SHOW_DEMO_TESTS')}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              {isAdvanceSetting && (
                <Collapse
                  defaultActiveKey={form.getFieldValue('showDemoTestsButton') ? ['demo'] : []}
                >
                  <Panel header={t('SCREENING_CUSTOM_STEP_DEMO_LABELS')} key="demo">
                    <div className="mb-3">
                      <div className="ant-alert-message">{`${t('PREVIEW')}:`}</div>
                      <Alert
                        type="info"
                        description={
                          <Form.Item noStyle shouldUpdate>
                            {() => {
                              const displayLanguage = getDisplayLanguage();
                              const subtitleBefore =
                                form.getFieldValue(`demo_subtitleBefore_${displayLanguage}`) ||
                                t('DEMO_DESCRIPTION_START');
                              const clickText =
                                form.getFieldValue(`demo_clickText_${displayLanguage}`) ||
                                t('DEMO_DESCRIPTION_CLICK');
                              const subtitleAfter =
                                form.getFieldValue(`demo_subtitleAfter_${displayLanguage}`) ||
                                t('DEMO_DESCRIPTION_END');

                              const demoTitle =
                                form.getFieldValue(`demo_title_${displayLanguage}`) ||
                                t('CONTINUE_TO_DEMO');

                              return (
                                <div style={{ marginTop: 8 }}>
                                  <div className="text-title font-weight-bold mb-2">
                                    {demoTitle}
                                  </div>
                                  <div>
                                    {subtitleBefore}
                                    <span
                                      style={{
                                        textDecoration: 'underline',
                                        color: '#1890ff',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      {' '}
                                      {clickText}
                                    </span>{' '}
                                    {subtitleAfter}
                                  </div>
                                </div>
                              );
                            }}
                          </Form.Item>
                        }
                      />
                    </div>
                    <div className="mb-3">
                      <label className="font-weight-600">
                        {t('SCREENING_CUSTOM_STEP_DEMO_TITLE')}
                      </label>
                      {renderTranslatedFields('demo_title', t('TITLE'))}
                    </div>

                    <div className="mb-3">
                      <label className="font-weight-600">
                        {t('SCREENING_CUSTOM_STEP_DEMO_SUBTITLE_BEFORE')}
                      </label>
                      {renderTranslatedFields('demo_subtitleBefore', t('SUBTITLE_BEFORE'))}
                    </div>

                    <div className="mb-3">
                      <label className="font-weight-600">
                        {t('SCREENING_CUSTOM_STEP_DEMO_CLICK_TEXT')}
                      </label>
                      {renderTranslatedFields('demo_clickText', t('CLICK_TEXT'))}
                    </div>

                    <div className="mb-3">
                      <label className="font-weight-600">
                        {t('SCREENING_CUSTOM_STEP_DEMO_SUBTITLE_AFTER')}
                      </label>
                      {renderTranslatedFields('demo_subtitleAfter', t('SUBTITLE_AFTER'))}
                    </div>
                  </Panel>
                </Collapse>
              )}
            </Panel>
          )}
        </Collapse>
      </Form>
    </Modal>
  );
};

export default ScreeningCustomStepModal;
